import { Release, ReleaseStatus, ReleaseType } from '../types';

interface GitHubRelease {
  id: number;
  tag_name: string;
  name: string;
  body: string;
  published_at: string;
  prerelease: boolean;
  draft: boolean;
  html_url: string;
}

/**
 * Extract owner and repo name from GitHub repository URL
 * Supports formats:
 * - https://github.com/owner/repo
 * - https://github.com/owner/repo/releases
 * - https://github.com/owner/repo/releases/tag/v1.0.0
 * - https://github.com/owner/repo.git
 */
export const parseGitHubUrl = (url: string): { owner: string; repo: string } | null => {
  try {
    // Clean up the URL
    let cleanUrl = url.trim();
    
    // Remove trailing slashes
    cleanUrl = cleanUrl.replace(/\/+$/, '');
    
    // Remove .git suffix if present
    cleanUrl = cleanUrl.replace(/\.git$/, '');
    
    const urlObj = new URL(cleanUrl);
    if (urlObj.hostname !== 'github.com' && urlObj.hostname !== 'www.github.com') {
      console.error('Not a GitHub URL:', urlObj.hostname);
      return null;
    }
    
    const pathParts = urlObj.pathname.split('/').filter(Boolean);
    
    if (pathParts.length >= 2) {
      const owner = pathParts[0];
      const repo = pathParts[1];
      
      console.log('Parsed GitHub URL:', { owner, repo, originalUrl: url });
      
      return { owner, repo };
    }
    
    console.error('Invalid GitHub URL path:', urlObj.pathname);
  } catch (error) {
    console.error('Failed to parse URL:', error);
  }
  return null;
};

/**
 * Fetch releases from GitHub repository
 */
export const fetchGitHubReleases = async (repoUrl: string): Promise<Release[]> => {
  const parsed = parseGitHubUrl(repoUrl);
  if (!parsed) {
    throw new Error('Invalid GitHub repository URL. Please use format: https://github.com/owner/repo');
  }

  const { owner, repo } = parsed;
  const apiUrl = `https://api.github.com/repos/${owner}/${repo}/releases`;

  console.log('Fetching releases from:', apiUrl);

  try {
    const headers: HeadersInit = {
      'Accept': 'application/vnd.github.v3+json',
    };

    // Add GitHub token for private repositories
    const githubToken = process.env.REACT_APP_GITHUB_TOKEN;
    if (githubToken) {
      headers['Authorization'] = `Bearer ${githubToken}`;
    }

    const response = await fetch(apiUrl, { 
      method: 'GET',
      headers,
      mode: 'cors'
    });

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      console.error('GitHub API error:', errorData);
      
      if (response.status === 404) {
        throw new Error(
          `Repository "${owner}/${repo}" not found or has no releases. ` +
          'Please check the URL and ensure the repository is public or add a GitHub token.'
        );
      } else if (response.status === 403) {
        throw new Error(
          'GitHub API rate limit exceeded. Please add a GitHub token (REACT_APP_GITHUB_TOKEN) to increase limits.'
        );
      } else if (response.status === 401) {
        throw new Error('GitHub authentication failed. Please check your GitHub token.');
      }
      
      throw new Error(`GitHub API error (${response.status}): ${response.statusText}`);
    }

    const githubReleases: GitHubRelease[] = await response.json();
    
    if (githubReleases.length === 0) {
      throw new Error(`No releases found for repository "${owner}/${repo}". Create some releases first.`);
    }
    
    console.log(`Found ${githubReleases.length} releases`);
    
    // Filter out draft releases and convert to our Release type
    const releases = githubReleases
      .filter(release => !release.draft)
      .map((release, index, array) => convertGitHubRelease(release, index, array.length));

    return releases;
  } catch (error) {
    if (error instanceof Error) {
      throw error;
    }
    throw new Error('Failed to fetch GitHub releases');
  }
};

/**
 * Convert GitHub release to our Release type
 * - Production releases: latest non-prerelease (Current), older non-prerelease (Past)
 * - Staging releases: prerelease versions (Current if latest prerelease, else Past)
 * - Development releases: Not automatically fetched (Planned)
 */
const convertGitHubRelease = (
  githubRelease: GitHubRelease,
  index: number,
  totalCount: number
): Release => {
  const releaseType: ReleaseType = githubRelease.prerelease ? 'Staging' : 'Production';
  
  // Determine status: first release of each type is Current, rest are Past
  let status: ReleaseStatus = 'Past';
  if (index === 0) {
    // Very first release is Current
    status = 'Current';
  } else {
    // Check if this is the first prerelease or first production release
    // For simplicity, we'll mark the latest of each type as Current
    // This logic can be refined based on specific requirements
  }

  // Extract version from tag_name (remove 'v' prefix if present)
  const version = githubRelease.tag_name.startsWith('v') 
    ? githubRelease.tag_name.substring(1) 
    : githubRelease.tag_name;

  // Use release body (release notes) as description, fall back to name or default
  const description = githubRelease.body || githubRelease.name || `Release ${version}`;

  return {
    id: githubRelease.id.toString(),
    version,
    description: description.trim(), // Full release notes without truncation
    releaseDate: githubRelease.published_at,
    releaseType,
    status,
    pullRequestUrl: githubRelease.html_url
  };
};

/**
 * Determine current releases by type
 * - Latest Production release -> Current Production
 * - Latest Staging (prerelease) -> Current Staging
 * - All others -> Past
 */
export const categorizeReleases = (releases: Release[]): Release[] => {
  if (releases.length === 0) return releases;

  // Find the latest production and staging releases
  const productionReleases = releases.filter(r => r.releaseType === 'Production');
  const stagingReleases = releases.filter(r => r.releaseType === 'Staging');

  const latestProductionId = productionReleases.length > 0 ? productionReleases[0].id : null;
  const latestStagingId = stagingReleases.length > 0 ? stagingReleases[0].id : null;

  // Update status based on latest releases
  return releases.map(release => ({
    ...release,
    status: 
      release.id === latestProductionId || release.id === latestStagingId
        ? 'Current' as ReleaseStatus
        : 'Past' as ReleaseStatus
  }));
};

/**
 * Parse GitHub Projects URL to extract org/user and project number
 */
export const parseGitHubProjectsUrl = (url: string): { owner: string; projectNumber: number; isOrg: boolean } | null => {
  try {
    const cleanUrl = url.trim().replace(/\/+$/, '');
    const urlObj = new URL(cleanUrl);
    
    if (urlObj.hostname !== 'github.com' && urlObj.hostname !== 'www.github.com') {
      return null;
    }
    
    // Match patterns:
    // https://github.com/orgs/ORG_NAME/projects/PROJECT_NUMBER
    // https://github.com/users/USERNAME/projects/PROJECT_NUMBER
    const orgMatch = cleanUrl.match(/\/orgs\/([^/]+)\/projects\/(\d+)/);
    const userMatch = cleanUrl.match(/\/users\/([^/]+)\/projects\/(\d+)/);
    
    if (orgMatch) {
      return { owner: orgMatch[1], projectNumber: parseInt(orgMatch[2]), isOrg: true };
    } else if (userMatch) {
      return { owner: userMatch[1], projectNumber: parseInt(userMatch[2]), isOrg: false };
    }
    
    return null;
  } catch (error) {
    console.error('Failed to parse GitHub Projects URL:', error);
    return null;
  }
};

interface WorkItem {
  id: string;
  title: string;
  url: string;
  status: string;
}

/**
 * Fetch work items from GitHub Projects based on status
 */
export const fetchGitHubProjectWorkItems = async (
  projectsUrl: string,
  statusFilter?: string[]
): Promise<WorkItem[]> => {
  const parsed = parseGitHubProjectsUrl(projectsUrl);
  if (!parsed) {
    throw new Error('Invalid GitHub Projects URL');
  }

  const { owner, projectNumber, isOrg } = parsed;
  const githubToken = process.env.REACT_APP_GITHUB_TOKEN;

  if (!githubToken) {
    throw new Error('GitHub token is required. Please set REACT_APP_GITHUB_TOKEN in your .env file.');
  }

  try {
    // Use GraphQL API to fetch project items
    const query = `
      query($owner: String!, $number: Int!) {
        ${isOrg ? 'organization' : 'user'}(login: $owner) {
          projectV2(number: $number) {
            items(first: 100) {
              nodes {
                id
                content {
                  ... on Issue {
                    title
                    url
                    number
                  }
                  ... on PullRequest {
                    title
                    url
                    number
                  }
                }
                fieldValues(first: 20) {
                  nodes {
                    ... on ProjectV2ItemFieldSingleSelectValue {
                      name
                      field {
                        ... on ProjectV2SingleSelectField {
                          name
                        }
                      }
                    }
                  }
                }
              }
            }
          }
        }
      }
    `;

    const response = await fetch('https://api.github.com/graphql', {
      method: 'POST',
      headers: {
        'Authorization': `Bearer ${githubToken}`,
        'Content-Type': 'application/json'
      },
      body: JSON.stringify({
        query,
        variables: { owner, number: projectNumber }
      })
    });

    if (!response.ok) {
      const errorText = await response.text();
      console.error('GitHub GraphQL API error:', response.status, errorText);
      throw new Error(`GitHub API error: ${response.status} - ${errorText}`);
    }

    const data = await response.json();
    
    // Check for GraphQL errors
    if (data.errors) {
      console.error('GitHub GraphQL errors:', data.errors);
      
      // Check if it's a scope permission error
      const scopeError = data.errors.find((e: any) => 
        e.message && e.message.includes('read:project')
      );
      
      if (scopeError) {
        console.warn('⚠️ GitHub token missing "read:project" scope. Please update your token at: https://github.com/settings/tokens');
        throw new Error('GitHub token missing required scope: "read:project". Please update your token permissions.');
      }
      
      throw new Error(`GraphQL errors: ${data.errors.map((e: any) => e.message).join(', ')}`);
    }
    
    // Check if data exists
    if (!data.data) {
      console.error('No data returned from GitHub API:', data);
      throw new Error('No data returned from GitHub API');
    }
    
    const ownerData = data.data[isOrg ? 'organization' : 'user'];
    if (!ownerData) {
      console.error(`Owner not found: ${owner} (isOrg: ${isOrg})`);
      throw new Error(`${isOrg ? 'Organization' : 'User'} '${owner}' not found or inaccessible`);
    }
    
    const projectData = ownerData.projectV2;
    if (!projectData) {
      console.error(`Project not found: ${projectNumber}`);
      throw new Error(`Project ${projectNumber} not found or inaccessible for ${owner}`);
    }

    const workItems: WorkItem[] = [];
    
    if (!projectData.items || !projectData.items.nodes) {
      console.warn('No items found in project');
      return workItems;
    }
    
    for (const item of projectData.items.nodes) {
      if (!item || !item.content) continue;
      
      // Find status field
      let status = '';
      if (item.fieldValues && item.fieldValues.nodes) {
        for (const fieldValue of item.fieldValues.nodes) {
          if (fieldValue && fieldValue.field?.name === 'Status') {
            status = fieldValue.name || '';
            break;
          }
        }
      }
      
      // Filter by status if provided (case-insensitive and trim whitespace)
      if (statusFilter && statusFilter.length > 0) {
        const normalizedStatus = status.trim().toLowerCase();
        const normalizedFilters = statusFilter.map(f => f.trim().toLowerCase());
        
        if (!normalizedFilters.includes(normalizedStatus)) {
          continue;
        }
      }
      
      workItems.push({
        id: item.id,
        title: item.content.title || 'Untitled',
        url: item.content.url || '',
        status
      });
    }
    
    return workItems;
  } catch (error) {
    console.error('Error fetching GitHub Project work items:', error);
    if (error instanceof Error) {
      throw error;
    }
    throw new Error('Failed to fetch GitHub Project work items');
  }
};

/**
 * Increment semantic version
 * Examples:
 * - 1.2.1 -> 1.2.2
 * - 1.2.9 -> 1.3.0
 * - 1.9.9 -> 2.0.0
 */
export const incrementVersion = (version: string): string => {
  // Remove 'v' prefix if present
  const cleanVersion = version.startsWith('v') ? version.substring(1) : version;
  
  const parts = cleanVersion.split('.').map(p => parseInt(p, 10));
  
  if (parts.length !== 3 || parts.some(isNaN)) {
    // If not a valid semantic version, just append .1
    return `${version}.1`;
  }
  
  let [major, minor, patch] = parts;
  
  // Increment patch
  patch++;
  
  // Handle rollover
  if (patch >= 10) {
    patch = 0;
    minor++;
  }
  
  if (minor >= 10) {
    minor = 0;
    major++;
  }
  
  return `${major}.${minor}.${patch}`;
};

/**
 * Calculate the nth occurrence of a weekday in a given month
 * @param year - The year
 * @param month - The month (0-11, where 0 is January)
 * @param weekday - The day of the week (0-6, where 0 is Sunday, 4 is Thursday)
 * @param occurrence - Which occurrence (1 = first, 2 = second, etc.)
 * @returns The date of the nth weekday
 */
const getNthWeekdayOfMonth = (year: number, month: number, weekday: number, occurrence: number): Date => {
  // Start with the first day of the month
  const firstDay = new Date(year, month, 1);
  const firstWeekday = firstDay.getDay();
  
  // Calculate the first occurrence of the desired weekday
  let firstOccurrenceDay = 1 + ((weekday - firstWeekday + 7) % 7);
  
  // Calculate the nth occurrence
  const targetDay = firstOccurrenceDay + (occurrence - 1) * 7;
  
  return new Date(year, month, targetDay);
};

/**
 * Calculate staging release date (2nd Thursday of the month following the current release)
 * @param currentReleaseDate - The date of the current release
 * @returns The date for the staging release
 */
const calculateStagingReleaseDate = (currentReleaseDate: Date): Date => {
  // Get next month
  const nextMonth = new Date(currentReleaseDate);
  nextMonth.setMonth(nextMonth.getMonth() + 1);
  
  // Get 2nd Thursday of that month (Thursday = 4)
  return getNthWeekdayOfMonth(nextMonth.getFullYear(), nextMonth.getMonth(), 4, 2);
};

/**
 * Calculate production release date (3rd Thursday of the month following the current release)
 * @param currentReleaseDate - The date of the current release
 * @returns The date for the production release
 */
const calculateProductionReleaseDate = (currentReleaseDate: Date): Date => {
  // Get next month
  const nextMonth = new Date(currentReleaseDate);
  nextMonth.setMonth(nextMonth.getMonth() + 1);
  
  // Get 3rd Thursday of that month (Thursday = 4)
  return getNthWeekdayOfMonth(nextMonth.getFullYear(), nextMonth.getMonth(), 4, 3);
};

/**
 * Generate planned releases based on work items from GitHub Projects
 * - Ready For QA items -> version with (s) suffix (staging)
 * - Ready For Prod items -> next production version
 * - Staging release: 2nd Thursday of the month following the current release
 * - Production release: 3rd Thursday of the month following the current release
 */
export const generatePlannedReleases = async (
  projectsUrl: string,
  currentVersion: string,
  applicationName: string,
  currentReleaseDate?: string
): Promise<Release[]> => {
  const plannedReleases: Release[] = [];
  
  if (!projectsUrl || !projectsUrl.trim()) {
    console.warn('No GitHub Projects URL provided');
    return plannedReleases;
  }
  
  try {
    console.log(`Fetching work items from GitHub Projects: ${projectsUrl}`);
    
    // Fetch work items with specific statuses
    let readyForQA: WorkItem[] = [];
    let readyForProd: WorkItem[] = [];
    
    try {
      // First, fetch ALL items to see what statuses exist
      const allItems = await fetchGitHubProjectWorkItems(projectsUrl);
      const uniqueStatuses = Array.from(new Set(allItems.map(item => item.status).filter(Boolean)));
      console.log(`📊 Found ${allItems.length} total items with these statuses:`, uniqueStatuses);
      
      // Log each item with its status for debugging
      allItems.forEach((item, index) => {
        if (index < 5) { // Log first 5 items
          console.log(`  Item ${index + 1}: "${item.title}" - Status: "${item.status}"`);
        }
      });
      
      // Try multiple variations of "Ready for QA" status
      readyForQA = await fetchGitHubProjectWorkItems(projectsUrl, [
        'Ready For QA', 
        'Ready for QA', 
        'Ready for qa',
        'READY FOR QA',
        'Ready For Qa',
        'ReadyForQA'
      ]);
      console.log(`Found ${readyForQA.length} items in "Ready For QA" (tried multiple variations)`);
    } catch (error) {
      if (error instanceof Error && error.message.includes('read:project')) {
        console.warn('⚠️ Skipping planned releases: GitHub token needs "read:project" scope');
        console.warn('📝 To enable planned releases:');
        console.warn('   1. Go to https://github.com/settings/tokens');
        console.warn('   2. Edit your token or create a new one');
        console.warn('   3. Enable the "read:project" scope');
        console.warn('   4. Update REACT_APP_GITHUB_TOKEN in your .env file');
        return plannedReleases; // Exit early - don't try other statuses
      }
      console.error('Error fetching Ready For QA items:', error);
    }
    
    try {
      // Try multiple variations of "Ready for Prod" status
      readyForProd = await fetchGitHubProjectWorkItems(projectsUrl, [
        'Ready For Prod', 
        'Ready for Prod',
        'Ready for prod',
        'READY FOR PROD',
        'Ready For Production',
        'Ready for Production',
        'ReadyForProd'
      ]);
      console.log(`Found ${readyForProd.length} items in "Ready For Prod" (tried multiple variations)`);
    } catch (error) {
      if (error instanceof Error && error.message.includes('read:project')) {
        return plannedReleases; // Exit early if scope error
      }
      console.error('Error fetching Ready For Prod items:', error);
    }
    
    // Use the provided current release date or default to today
    const baseDate = currentReleaseDate ? new Date(currentReleaseDate) : new Date();
    
    // Calculate staging release date (2nd Thursday of next month)
    const stagingDate = calculateStagingReleaseDate(baseDate);
    
    // Calculate production release date (3rd Thursday of next month)
    const productionDate = calculateProductionReleaseDate(baseDate);
    
    // Generate staging release (Ready For QA)
    if (readyForQA.length > 0) {
      const stagingVersion = `${incrementVersion(currentVersion)}(s)`;
      const workItemLinks = readyForQA
        .filter((item: WorkItem) => item.title && item.url)
        .map((item: WorkItem) => `- [${item.title}](${item.url})`)
        .join('\n');
      
      if (workItemLinks) {
        plannedReleases.push({
          id: `planned-staging-${Date.now()}`,
          version: stagingVersion,
          description: `## Ready For QA\n\n${workItemLinks}`,
          releaseDate: stagingDate.toISOString(),
          releaseType: 'Staging',
          status: 'Planned'
        });
        console.log(`Generated staging release: ${stagingVersion} for ${stagingDate.toDateString()}`);
      }
    }
    
    // Generate production release (Ready For Prod)
    if (readyForProd.length > 0) {
      const productionVersion = incrementVersion(readyForQA.length > 0 ? incrementVersion(currentVersion) : currentVersion);
      const workItemLinks = readyForProd
        .filter((item: WorkItem) => item.title && item.url)
        .map((item: WorkItem) => `- [${item.title}](${item.url})`)
        .join('\n');
      
      if (workItemLinks) {
        plannedReleases.push({
          id: `planned-production-${Date.now()}`,
          version: productionVersion,
          description: `## Ready For Production\n\n${workItemLinks}`,
          releaseDate: productionDate.toISOString(),
          releaseType: 'Production',
          status: 'Planned'
        });
        console.log(`Generated production release: ${productionVersion} for ${productionDate.toDateString()}`);
      }
    }
    
    console.log(`Generated ${plannedReleases.length} planned releases`);
    return plannedReleases.slice(0, 2); // Only return first 2
  } catch (error) {
    console.error('Error generating planned releases:', error);
    if (error instanceof Error) {
      console.error('Error details:', error.message);
    }
    return [];
  }
};
